import { useEffect, useState, useContext } from 'react';
import _ from 'lodash';
import { Row, Col, Button } from 'react-bootstrap';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import LoadingPage from '../LoadingPage';
import ApiService from '../../services/ApiService';
import ModalList from './ModalList';
import ModalTable from './ModalTable';
import SectionHeading from './SectionHeading';
import MiniGallery from '../attachments/MiniGallery';
import ClickToCopy from '../ClickToCopy';
import { popProperty, titleCase } from '../../util/general';
import { statusColors, getStatus } from '../../util/vehicle';
import ModalContext from './ModalContext';
import { modal } from './ModalEmitter';
import PdfWrapper from '../print/PdfWrapper';

const detailHeaders = [
  {
    text: 'Source',
    width: 80,
    className: 'text-center',
  },
  {
    text: 'Reference',
    width: 115,
    helper: 'viewInternalRepairOrder',
  },
  {
    text: 'Invoice',
    width: 115,
  },
  {
    text: 'Date',
    width: 85,
    className: 'text-center',
  },
  {
    text: 'Account',
    width: 85,
    className: 'text-center',
  },
  {
    text: 'Amount',
    width: 85,
    className: 'text-right',
  },
  {
    text: 'Memo',
    width: 255,
  },
  {
    text: 'Currency',
    width: 85,
  },
];

const VehicleSpecSheet = ({ lookupKey, view = 'vehicleSpecSheet' }) => {
  const [loading, setLoading] = useState(true);
  const [primaryData, setPrimaryData] = useState({});
  const [repairOrderHeaders, setRepairOrderHeaders] = useState([]);
  const [purchaseOrderHeaders, setPurchaseOrderHeaders] = useState([]);
  const [unitTitle, setUnitTitle] = useState('');
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const { setTitle } = useContext(ModalContext);

  const getData = () => {
    setLoading(true);

    ApiService.get(`drilldown/${view}/${encodeURIComponent(lookupKey)}`).then(response => {
      const { data } = response.data;

      const specs = JSON.parse(data);

      setPurchaseOrderHeaders(
        specs.openPurchaseOrders.length ? Object.keys(specs.openPurchaseOrders[0]).map(k => titleCase(k)) : []
      );
      setRepairOrderHeaders(
        specs.repairOrderHistory.length ? Object.keys(specs.repairOrderHistory[0]).map(k => titleCase(k)) : []
      );

      specs.detailedCosts = specs.detailedCosts.map(r => Object.keys(r).map(k => r[k]));
      specs.repairOrderHistory = specs.repairOrderHistory.map(r => Object.keys(r).map(k => r[k]));
      specs.openPurchaseOrders = specs.openPurchaseOrders.map(r => Object.keys(r).map(k => r[k]));

      setUnitTitle(_.get(specs, 'vehicleSpecs.Make'));

      const [salesStatus, vehicleState, vehicleYear, vehicleMake, vehicleModel, vin, vehicleType] = popProperty(
        specs.vehicleSpecs,
        ['New/Used', 'Status(A)', 'Year', 'Make', 'Model', 'VIN', 'Vehicle Type']
      );

      const [custNum, custName, custAddress, custAddressCsz] = popProperty(specs.vehicleSpecs, [
        'Cust Number',
        'Customer Name',
        'Cust Address',
        'Cust Csz',
      ]);

      const [salesman, salesPrice, listPrice, salesDate] = popProperty(specs.vehicleSpecs, [
        'Salesman',
        'Selling Price',
        'List Price',
        'Date Sold',
      ]);

      specs.basicSpecs = {
        salesStatus,
        vehicleState,
        vehicleYear,
        vehicleMake,
        vehicleModel,
        vin,
        vehicleType,
        salesman,
        salesPrice,
        listPrice,
        salesDate,
        custNum,
        custName,
        custAddress,
        custAddressCsz,
      };

      setPrimaryData(specs);
      setLoading(false);
    });
  };

  const getPdf = () => {
    setGeneratingPdf(true);
    ApiService.get(`vehicle/${encodeURIComponent(lookupKey)}/spec-sheet`)
      .then(response => {
        modal.open({
          title: 'Spec Sheet PDF',
          style: 'fitted',
          hidePrint: true,
          hideFooter: true,
          staticBackdrop: true,
          message: <PdfWrapper url={response.data} initialRecipients={[]} initialSubject={unitTitle} />,
          // message: <PdfObject url={response.data} width="100%" height={window.innerHeight * 0.8} />,
        });
      })
      .finally(() => setGeneratingPdf(false));
  };

  useEffect(() => {
    getData();
    setTitle(`Vehicle Cost Sheet (${lookupKey})`);
  }, []);

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <div>
      <Row>
        <Col md={6}>
          <MiniGallery itemName="AVI2" itemId={lookupKey} />
          <div className="vin text-center h5 p-2">
            <span>{primaryData.basicSpecs.vehicleType}</span>
            <span>
              <ClickToCopy>{primaryData.basicSpecs.vin}</ClickToCopy>
            </span>
          </div>
        </Col>
        <Col md={6} className="d-flex flex-column">
          <div className="entity-title">
            <div>
              <div
                className={classNames('status h6 float-left p-2 mb-0')}
                style={{ backgroundColor: statusColors[getStatus(primaryData.basicSpecs.salesStatus)] }}
              >
                {primaryData.basicSpecs.salesStatus}
              </div>
              <div
                className={classNames('status h6 float-left p-2 mb-0 ml-1')}
                style={{ backgroundColor: statusColors[getStatus(primaryData.basicSpecs.vehicleState)] }}
              >
                {primaryData.basicSpecs.vehicleState}
              </div>
            </div>
            <div className="title mb-0">
              {primaryData.basicSpecs.vehicleYear} {primaryData.basicSpecs.vehicleMake}{' '}
              {primaryData.basicSpecs.vehicleModel}
            </div>
          </div>
          <hr className="m-1" />
          <div style={{ overflowY: 'auto', height: '275px', flexGrow: '1' }}>
            <ModalList data={primaryData.vehicleSpecs} columns={2} />
          </div>
          {primaryData.basicSpecs.vehicleState === 'SOLD' && (
            <div className="sales-section">
              <div className="d-inline-block sales">
                <strong>Salesperson</strong> {primaryData.basicSpecs.salesman}
                {primaryData.basicSpecs.salesPrice && (
                  <div>
                    <strong>S</strong> {primaryData.basicSpecs.salesPrice} ({primaryData.basicSpecs.salesDate})
                  </div>
                )}
                {primaryData.basicSpecs.listPrice && (
                  <div>
                    <strong>L</strong> {primaryData.basicSpecs.listPrice}
                  </div>
                )}
              </div>
              <div className="d-inline-block customer">
                {primaryData.basicSpecs.custName}{' '}
                {primaryData.basicSpecs.custNum && `(${primaryData.basicSpecs.custNum})`}
                {primaryData.basicSpecs.custAddress && <div>{primaryData.basicSpecs.custAddress}</div>}
                {primaryData.basicSpecs.custAddressCsz && <div>{primaryData.basicSpecs.custAddressCsz}</div>}
              </div>
            </div>
          )}
          <div className="text-right">
            <Button variant="default" onClick={() => getPdf()} disabled={generatingPdf}>
              {generatingPdf ? (
                <>
                  <i className="fad fa-spinner-third fa-spin" /> Generating PDF
                </>
              ) : (
                <>Generate PDF</>
              )}
            </Button>
          </div>
        </Col>
        <Col md={12}>
          <SectionHeading>Cost Details</SectionHeading>
          <ModalTable
            hideToolbar
            headers={detailHeaders}
            data={primaryData.detailedCosts}
            style={{ height: '200px' }}
            controlId="cost"
          />
        </Col>
        <Col md={6}>
          <SectionHeading>Open Purchase Orders</SectionHeading>
          <ModalTable
            hideToolbar
            headers={purchaseOrderHeaders}
            data={primaryData.openPurchaseOrders}
            style={{ height: '200px' }}
            controlId="open"
          />
        </Col>
        <Col md={6}>
          <SectionHeading>Repair Order History</SectionHeading>
          <ModalTable
            hideToolbar
            headers={repairOrderHeaders}
            data={primaryData.repairOrderHistory}
            style={{ height: '200px' }}
            controlId="repair"
          />
        </Col>
      </Row>
    </div>
  );
};

VehicleSpecSheet.propTypes = {
  lookupKey: PropTypes.string,
  view: PropTypes.string,
};

export default VehicleSpecSheet;
